import { RideUpdate, LocationUpdate, RideUpdateSchema, LocationUpdateSchema } from '@trackthatride/core';

export interface SSEConnectionConfig {
  apiKey?: string;
  baseUrl?: string;
  maxRetries?: number;
  retryDelay?: number;
  enableLogging?: boolean;
}

export interface SSEEventMap {
  'ride-update': RideUpdate;
  'location-update': LocationUpdate;
  'driver-status': { driverId: number; status: string; timestamp: string };
  'eta-update': { rideId: number; estimatedArrival: string; timestamp: string };
  'connection-status': { status: 'connected' | 'reconnecting' | 'disconnected'; timestamp: string };
  'error': { message: string; code?: string; timestamp: string };
}

export type SSEEventHandler<K extends keyof SSEEventMap> = (data: SSEEventMap[K]) => void;

export class TrackingSSEClient {
  private eventSource: EventSource | null = null;
  private listeners: Map<string, Set<Function>> = new Map();
  private config: SSEConnectionConfig & { baseUrl: string; maxRetries: number; retryDelay: number; enableLogging: boolean };
  private retryCount = 0;
  private retryTimeout: NodeJS.Timeout | null = null;
  private isManualClose = false;

  constructor(config: SSEConnectionConfig = {}) {
    // Ensure baseUrl is required and provided by integrator
    if (!config.baseUrl) {
      throw new Error('baseUrl is required. Please provide your Track That Ride API base URL (e.g., "https://your-app.replit.app")');
    }
    
    this.config = {
      baseUrl: config.baseUrl,
      maxRetries: 5,
      retryDelay: 2000,
      enableLogging: false,
      ...config
    };
  }

  /**
   * Connect to public tracking events for a specific tracking code
   */
  connectToPublicTracking(trackingCode: string): void {
    const url = `${this.config.baseUrl}/api/public/track/${trackingCode}/events`;
    this.connect(url);
  }

  /**
   * Connect to authenticated tracking events (requires API key)
   * Note: For security, this requires server-side implementation that generates
   * a temporary, signed access token for SSE connections to avoid exposing API keys in URLs
   */
  connectToPrivateTracking(companyId?: number): void {
    if (!this.config.apiKey) {
      throw new Error('API key required for private tracking connection');
    }
    
    // First, get a secure SSE token from the server
    this.getSecureSSEToken(companyId)
      .then(token => {
        const url = companyId 
          ? `${this.config.baseUrl}/api/v1/tracking/events?companyId=${companyId}&token=${token}`
          : `${this.config.baseUrl}/api/v1/tracking/events?token=${token}`;
        
        this.connect(url);
      })
      .catch(error => {
        this.emit('error', {
          message: `Failed to get secure SSE token: ${error.message}`,
          code: 'SSE_TOKEN_ERROR',
          timestamp: new Date().toISOString()
        });
      });
  }

  /**
   * Exchanges API key for a secure, time-limited SSE token
   * This prevents API keys from appearing in URLs, logs, or caches
   */
  private async getSecureSSEToken(companyId?: number): Promise<string> {
    const response = await fetch(`${this.config.baseUrl}/api/v1/auth/sse-token`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.config.apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        companyId,
        purpose: 'tracking-events',
        expiresIn: 3600 // 1 hour
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to get SSE token: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    if (!data.success || !data.token) {
      throw new Error('Invalid SSE token response');
    }

    return data.token;
  }

  private connect(url: string): void {
    this.disconnect();
    this.isManualClose = false;
    
    try {
      // Standard EventSource - no headers needed as security is handled via temporary token
      this.eventSource = new EventSource(url);
      this.setupEventListeners();
      
      if (this.config.enableLogging) {
        // Log URL without sensitive parameters
        const logUrl = new URL(url);
        if (logUrl.searchParams.has('token')) {
          logUrl.searchParams.set('token', '[REDACTED]');
        }
        console.log('[TrackingSSE] Connecting to:', logUrl.toString());
      }
    } catch (error) {
      this.emit('error', { 
        message: error instanceof Error ? error.message : 'Connection failed',
        timestamp: new Date().toISOString()
      });
    }
  }

  private setupEventListeners(): void {
    if (!this.eventSource) return;

    this.eventSource.onopen = () => {
      this.retryCount = 0;
      this.emit('connection-status', { 
        status: 'connected', 
        timestamp: new Date().toISOString() 
      });
      
      if (this.config.enableLogging) {
        console.log('[TrackingSSE] Connected successfully');
      }
    };

    this.eventSource.onerror = (event) => {
      if (this.isManualClose) return;
      
      this.emit('connection-status', { 
        status: 'disconnected', 
        timestamp: new Date().toISOString() 
      });
      
      if (this.config.enableLogging) {
        console.error('[TrackingSSE] Connection error:', event);
      }
      
      this.handleReconnect();
    };

    this.eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleMessage(data);
      } catch (error) {
        this.emit('error', {
          message: 'Failed to parse message data',
          timestamp: new Date().toISOString()
        });
      }
    };

    // Custom event types
    this.setupCustomEventHandlers();
  }

  private setupCustomEventHandlers(): void {
    if (!this.eventSource) return;

    // Ride updates
    this.eventSource.addEventListener('ride-update', (event) => {
      try {
        const data = JSON.parse(event.data);
        const validatedData = RideUpdateSchema.parse(data);
        this.emit('ride-update', validatedData);
      } catch (error) {
        if (this.config.enableLogging) {
          console.error('[TrackingSSE] Invalid ride-update data:', error);
        }
      }
    });

    // Location updates
    this.eventSource.addEventListener('location-update', (event) => {
      try {
        const data = JSON.parse(event.data);
        const validatedData = LocationUpdateSchema.parse(data);
        this.emit('location-update', validatedData);
      } catch (error) {
        if (this.config.enableLogging) {
          console.error('[TrackingSSE] Invalid location-update data:', error);
        }
      }
    });

    // Driver status updates
    this.eventSource.addEventListener('driver-status', (event) => {
      try {
        const data = JSON.parse(event.data);
        this.emit('driver-status', data);
      } catch (error) {
        if (this.config.enableLogging) {
          console.error('[TrackingSSE] Invalid driver-status data:', error);
        }
      }
    });

    // ETA updates
    this.eventSource.addEventListener('eta-update', (event) => {
      try {
        const data = JSON.parse(event.data);
        this.emit('eta-update', data);
      } catch (error) {
        if (this.config.enableLogging) {
          console.error('[TrackingSSE] Invalid eta-update data:', error);
        }
      }
    });
  }

  private handleMessage(data: any): void {
    // Handle generic messages that don't have specific event types
    if (data.type) {
      switch (data.type) {
        case 'ping':
          // Handle ping/keepalive
          break;
        case 'error':
          this.emit('error', {
            message: data.message || 'Server error',
            code: data.code,
            timestamp: new Date().toISOString()
          });
          break;
        default:
          if (this.config.enableLogging) {
            console.log('[TrackingSSE] Unknown message type:', data.type);
          }
      }
    }
  }

  private handleReconnect(): void {
    if (this.isManualClose || this.retryCount >= this.config.maxRetries) {
      this.emit('error', {
        message: 'Max retry attempts reached',
        code: 'MAX_RETRIES_EXCEEDED',
        timestamp: new Date().toISOString()
      });
      return;
    }

    this.retryCount++;
    const delay = this.config.retryDelay * Math.pow(2, this.retryCount - 1); // Exponential backoff
    
    this.emit('connection-status', { 
      status: 'reconnecting', 
      timestamp: new Date().toISOString() 
    });
    
    if (this.config.enableLogging) {
      console.log(`[TrackingSSE] Reconnecting in ${delay}ms (attempt ${this.retryCount}/${this.config.maxRetries})`);
    }
    
    this.retryTimeout = setTimeout(() => {
      // Attempt to reconnect with the same URL
      if (this.eventSource) {
        const url = this.eventSource.url;
        this.connect(url);
      }
    }, delay);
  }

  /**
   * Subscribe to a specific event type
   */
  on<K extends keyof SSEEventMap>(event: K, handler: SSEEventHandler<K>): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
  }

  /**
   * Unsubscribe from a specific event type
   */
  off<K extends keyof SSEEventMap>(event: K, handler: SSEEventHandler<K>): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Subscribe to an event only once
   */
  once<K extends keyof SSEEventMap>(event: K, handler: SSEEventHandler<K>): void {
    const onceHandler = (data: SSEEventMap[K]) => {
      handler(data);
      this.off(event, onceHandler as SSEEventHandler<K>);
    };
    this.on(event, onceHandler as SSEEventHandler<K>);
  }

  private emit<K extends keyof SSEEventMap>(event: K, data: SSEEventMap[K]): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          (handler as SSEEventHandler<K>)(data);
        } catch (error) {
          if (this.config.enableLogging) {
            console.error(`[TrackingSSE] Error in event handler for ${event}:`, error);
          }
        }
      });
    }
  }

  /**
   * Get current connection state
   */
  getReadyState(): number {
    return this.eventSource?.readyState ?? EventSource.CLOSED;
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN;
  }

  /**
   * Disconnect from the SSE stream
   */
  disconnect(): void {
    this.isManualClose = true;
    
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    
    this.retryCount = 0;
    
    if (this.config.enableLogging) {
      console.log('[TrackingSSE] Disconnected');
    }
  }

  /**
   * Remove all event listeners
   */
  removeAllListeners(): void {
    this.listeners.clear();
  }
}